import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useSearchParams } from "react-router-dom";
import {
	createCourseTemplate,
	deleteCourseTemplate,
	listCourseTemplates,
	updateCourseTemplate,
	getCourseSatisfactionSummary,
} from "../api/courses";
import { listUsers } from "../api/users";
import {
	capitalizeFirstLetter,
	capitalizeWords,
	RANDOM_COURSE_IMAGES,
} from "../../utils/letters";
import Modal from "../components/Modal";
import ConfirmModal from "../components/ConfirmModal";
import StatusBadge from "../components/StatusBadge";
import WeeklyScheduleModal from "../components/WeeklyScheduleModal";
import { formatWeeklyScheduleForDisplay } from "../../utils/weeklySchedule";
import RichTextEditor from "../components/RichTextEditor";
import { convertToWebp } from "../../utils/image";
import { formatDate } from "../../utils/date";

async function getCourseImageFile(imagePath) {
	// 1. Extraemos el nombre del archivo
	const fileName = imagePath.split("/").pop() || "default.webp";

	// 2. Hacemos el fetch de la imagen que le pasemos por parámetro
	const res = await fetch(imagePath);
	if (!res.ok) throw new Error("No se pudo cargar la imagen del curso.");

	const blob = await res.blob();
	return new File([blob], fileName, {
		type: blob.type || "image/webp",
	});
}

const emptyForm = {
	code: "",
	name: "",
	description: "",
	temario: "",
	price_type: "free",
	price_amount: "",
	duration_hours: "",
	objectives: "",
	is_active: true,
	// Run (asesoría): fechas, postulación, horario, docente, cupos
	modality: "presencial",
	start_date: "",
	end_date: "",
	schedule: "",
	graduation_requirements: "",
	teacher: "",
	enrollment_start_date: "",
	enrollment_end_date: "",
	enrollment_url: "https://isomnus.cl/admision",
	max_students: "",
	image: null,
};

const SAVE_FALLBACK_MESSAGE =
	"No se pudo guardar. Revisa código único y los campos obligatorios.";
const COURSES_ROWS_PER_PAGE_DEFAULT = 10;
const COURSES_ALLOWED_ROWS = [10, 25, 50, 100];

const COURSE_SORT_OPTIONS = [
	{ value: "code", label: "Código" },
	{ value: "name", label: "Nombre" },
	{ value: "modality", label: "Modalidad" },
	{ value: "enrollment_period", label: "Fecha postulación" },
	{ value: "organizations_enrollment", label: "Postulaciones" },
	{ value: "is_active", label: "Activo" },
	{ value: "organizations_open", label: "En curso" },
	{ value: "organizations_finished", label: "Finalizados" },
	{ value: "organizations_closed", label: "Cerrados" },
];

const COURSES_ALLOWED_SORTS = new Set(
	COURSE_SORT_OPTIONS.map((option) => option.value),
);
const COURSES_ALLOWED_DIRECTIONS = new Set(["asc", "desc"]);

function parsePositiveInt(rawValue, fallbackValue) {
	const parsed = Number(rawValue);
	if (!Number.isInteger(parsed) || parsed < 1) return fallbackValue;
	return parsed;
}

function parseRows(rawValue) {
	const parsed = Number(rawValue);
	if (!COURSES_ALLOWED_ROWS.includes(parsed)) {
		return COURSES_ROWS_PER_PAGE_DEFAULT;
	}
	return parsed;
}

function parseSort(rawValue) {
	if (!COURSES_ALLOWED_SORTS.has(rawValue)) return "name";
	return rawValue;
}

function parseDirection(rawValue) {
	if (!COURSES_ALLOWED_DIRECTIONS.has(rawValue)) return "asc";
	return rawValue;
}

function parseShowInactive(rawValue) {
	return rawValue === "1";
}

function isValidDateString(value) {
	if (!value) return false;
	const date = new Date(`${value}T00:00:00`);
	return !Number.isNaN(date.getTime());
}

function isValidHttpUrl(value) {
	try {
		const url = new URL(value);
		return url.protocol === "http:" || url.protocol === "https:";
	} catch {
		return false;
	}
}

function isAsyncCourseModality(modality) {
	return modality === "async";
}

function getFirstApiMessage(value) {
	if (Array.isArray(value)) return value[0] ?? "";
	if (typeof value === "string") return value;
	return "";
}

function getApiFieldErrors(data) {
	const next = {};
	if (!data || typeof data !== "object") return next;

	const keys = [
		"code",
		"name",
		"description",
		"temario",
		"image",
		"price_type",
		"price_amount",
		"duration_hours",
		"objectives",
		"modality",
		"start_date",
		"end_date",
		"schedule",
		"graduation_requirements",
		"teacher",
		"enrollment_start_date",
		"enrollment_end_date",
		"enrollment_url",
		"max_students",
	];

	for (const key of keys) {
		const message = getFirstApiMessage(data[key]);
		if (message) next[key] = message;
	}

	const detail =
		typeof data.detail === "string"
			? data.detail
			: Array.isArray(data.non_field_errors)
				? data.non_field_errors[0]
				: "";

	if (detail && !next.code && /codigo|code|unico|unique/i.test(detail)) {
		next.code = detail;
	}

	return next;
}

function getPlainTextFromHtml(html) {
	if (!html) return "";
	const doc = new DOMParser().parseFromString(html, 'text/html');
	return doc.body.textContent || "";
}

function escapeRegExp(value) {
	return value.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

function highlightSearchMatch(value, query) {
	const text = (value ?? "").toString();
	const needle = query.trim();
	if (!needle) return text;

	const matcher = new RegExp(`(${escapeRegExp(needle)})`, "ig");
	const parts = text.split(matcher);
	let cursor = 0;

	return parts.map((part) => {
		const key = `${cursor}-${part}`;
		cursor += part.length;
		if (part.toLowerCase() === needle.toLowerCase()) {
			return (
				<mark className="search-highlight" key={`hit-${key}`}>
					{part}
				</mark>
			);
		}
		return <span key={`txt-${key}`}>{part}</span>;
	});
}

function formatClpAmount(value) {
	const amount = Number(value);
	if (!Number.isFinite(amount)) return null;
	return new Intl.NumberFormat("es-CL").format(Math.round(amount));
}

function formatCoursePriceLabel(course) {
	if (course?.price_type === "free") return "Gratuito";
	const formattedAmount = formatClpAmount(course?.price_amount);
	return formattedAmount ? `$${formattedAmount}` : "—";
}

function formatDateRange(startDate, endDate) {
	const start = formatDate(startDate);
	const end = formatDate(endDate);
	if (start && end) return `${start} a ${end}`;
	return start || end || "Por definir";
}

export default function CoursesList() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [items, setItems] = useState([]);
	const [search, setSearch] = useState(() => searchParams.get("q") || "");
	const [loading, setLoading] = useState(false);
	const [rowsPerPage, setRowsPerPage] = useState(() =>
		parseRows(searchParams.get("rows")),
	);
	const [currentPage, setCurrentPage] = useState(() =>
		parsePositiveInt(searchParams.get("page"), 1),
	);
	const [sortField, setSortField] = useState(() =>
		parseSort(searchParams.get("sort")),
	);
	const [sortDirection, setSortDirection] = useState(() =>
		parseDirection(searchParams.get("dir")),
	);
	const [showInactiveCourses, setShowInactiveCourses] = useState(() =>
		parseShowInactive(searchParams.get("inactive")),
	);

	// alerts bootstrap
	const [alert, setAlert] = useState(null); // { type: "success"|"danger"|"warning", msg: string }

	// modal state
	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form, setForm] = useState(emptyForm);
	const [saving, setSaving] = useState(false);
	const [itemToDelete, setItemToDelete] = useState(null);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [itemToActivate, setItemToActivate] = useState(null);
	const [activateLoading, setActivateLoading] = useState(false);
	const [teachers, setTeachers] = useState([]);
	const [fieldErrors, setFieldErrors] = useState({});
	const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);

	const [surveySummaryData, setSurveySummaryData] = useState(null);
	const [surveySummaryLoading, setSurveySummaryLoading] = useState(false);
	const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);
	const [surveyModalTitle, setSurveyModalTitle] = useState("");

	const openSurveySummary = async (course) => {
		setSurveyModalTitle(course.name);
		setIsSurveyModalOpen(true);
		setSurveySummaryLoading(true);
		setSurveySummaryData(null);
		try {
			const data = await getCourseSatisfactionSummary(course.id);
			setSurveySummaryData(data);
		} catch (error) {
			console.error("Error obteniendo el resumen de la encuesta", error);
		} finally {
			setSurveySummaryLoading(false);
		}
	};

	// Modal de detalles del curso
	const [detailsOpen, setDetailsOpen] = useState(false);
	const [selectedCourseDetail, setSelectedCourseDetail] = useState(null);
	const [imagePreviewSrc, setImagePreviewSrc] = useState(null);

	async function refresh() {
		setLoading(true);
		try {
			const data = await listCourseTemplates({});
			setItems(Array.isArray(data) ? data : (data.results ?? []));
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudieron cargar los cursos del catálogo.",
			});
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!modalOpen) return;
		let cancelled = false;
		(async () => {
			try {
				const data = await listUsers({});
				const list = Array.isArray(data) ? data : (data.results ?? []);
				if (!cancelled)
					setTeachers(
						list.filter(
							(u) =>
								u.role === "teacher" &&
								u.is_active !== false,
						),
					);
			} catch {
				if (!cancelled) setTeachers([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [modalOpen]);

	function generateCode() {
		// Genera un código legible tipo CUR-001, CUR-XYZ, etc.
		const random = Math.random().toString(36).substring(2, 7).toUpperCase();
		return `CUR-${random}`.substring(0, 10);
	}

	function openCreate() {
		setEditing(null);
		setForm({
			...emptyForm,
			code: generateCode(),
			price_type: "free",
			price_amount: "",
			duration_hours: "",
			image: null,
		});
		setAlert(null);
		setFieldErrors({});
		setModalOpen(true);
	}

	function openEdit(item) {
		setEditing(item);
		const run = item.default_run || {};
		setForm({
			code: item.code ?? "",
			name: item.name ?? "",
			description: item.description ?? "",
			temario: item.temario ?? "",
			price_type: item.price_type ?? "free",
			price_amount: item.price_amount ?? "",
			duration_hours: item.duration_hours ?? "",
			objectives: item.objectives ?? "",
			is_active: item.is_active ?? true,
			modality: run.modality ?? "presencial",
			start_date: run.start_date ?? "",
			end_date: run.end_date ?? "",
			schedule: run.schedule ?? "",
			graduation_requirements: run.graduation_requirements ?? "",
			teacher: run.teacher ?? "",
			enrollment_start_date: run.enrollment_start_date ?? "",
			enrollment_end_date: run.enrollment_end_date ?? "",
			enrollment_url: "https://isomnus.cl/admision",
			max_students: run.max_students ?? "",
			image: null,
		});
		setAlert(null);
		setFieldErrors({});
		setModalOpen(true);
	}

	function openDetails(course) {
		setSelectedCourseDetail(course);
		setDetailsOpen(true);
	}

	function closeModal(forceDiscard = false) {
		const isExplicitDiscard = forceDiscard === true;
		if (saving) return;
		const hasContent = form.name.trim() || getPlainTextFromHtml(form.description).trim() || getPlainTextFromHtml(form.objectives).trim();
		const isNewOrDraft = !editing || (editing && !editing.is_active);

		if (!isExplicitDiscard && hasContent && isNewOrDraft) {
			onSubmit(null, true);
			return;
		}
		setModalOpen(false);
		setSchedulePickerOpen(false);
		setFieldErrors({});
		setEditing(null);
		setForm(emptyForm);
	}

	function validateForm() {
		const err = {};
		if (!(form.code ?? "").trim())
			err.code = "Código del curso es obligatorio.";
		else if (!/^[A-Za-z0-9-]{2,10}$/.test((form.code ?? "").trim()))
			err.code = "El código debe tener 2 a 10 caracteres y solo usar letras, números o guion.";
		if (!(form.name ?? "").trim())
			err.name = "Nombre del curso es obligatorio.";
		if (!(form.description ?? "").trim())
			err.description = "La descripción es obligatoria.";
		if (!(form.objectives ?? "").trim())
			err.objectives = "Los objetivos del curso son obligatorios.";
		if (form.duration_hours === "" || form.duration_hours == null)
			err.duration_hours = "La duración (horas) es obligatoria.";
		else if (Number(form.duration_hours) <= 0)
			err.duration_hours = "La duración debe ser mayor a 0.";

		if (!(form.modality ?? "").trim())
			err.modality = "La modalidad es obligatoria.";
		if (!(form.start_date ?? "").trim())
			err.start_date = "Fecha de inicio es obligatoria.";
		else if (!isValidDateString(form.start_date))
			err.start_date = "Fecha de inicio no válida.";
		if (!(form.end_date ?? "").trim())
			err.end_date = "Fecha de término es obligatoria.";
		else if (!isValidDateString(form.end_date))
			err.end_date = "Fecha de término no válida.";

		if (form.is_active) {
			if (!editing && (form.teacher == null || form.teacher === ""))
				err.teacher = "Selecciona un docente.";
			if (!(form.enrollment_start_date ?? "").trim())
				err.enrollment_start_date = "Fecha de inicio es obligatoria.";
			else if (!isValidDateString(form.enrollment_start_date))
				err.enrollment_start_date = "Fecha de inicio de postulación no válida.";
			if (!(form.enrollment_end_date ?? "").trim())
				err.enrollment_end_date = "Fecha de cierre es obligatoria.";
			if (form.enrollment_end_date && !isValidDateString(form.enrollment_end_date))
				err.enrollment_end_date = "Fecha de cierre de postulación no válida.";
			if (form.enrollment_url && !isValidHttpUrl(form.enrollment_url))
				err.enrollment_url = "Ingresa una URL de postulación válida.";
			if (form.max_students === "" || form.max_students == null)
				err.max_students = "Los cupos son obligatorios.";
			else if (Number(form.max_students) <= 0)
				err.max_students = "Los cupos deben ser mayor a 0.";
		} else {
			if (form.enrollment_start_date && !isValidDateString(form.enrollment_start_date))
				err.enrollment_start_date = "Fecha de inicio de postulación no válida.";
			if (form.enrollment_end_date && !isValidDateString(form.enrollment_end_date))
				err.enrollment_end_date = "Fecha de cierre de postulación no válida.";
			if (form.enrollment_url && !isValidHttpUrl(form.enrollment_url))
				err.enrollment_url = "Ingresa una URL de postulación válida.";
			if (form.max_students !== "" && form.max_students != null && Number(form.max_students) <= 0)
				err.max_students = "Los cupos deben ser mayor a 0.";
		}

		if (form.price_type === "paid") {
			if (form.price_amount === "" || form.price_amount == null) {
				err.price_amount = "El precio es obligatorio.";
			} else if (Number(form.price_amount) <= 0) {
				err.price_amount = "El precio debe ser mayor a 0.";
			}
		}
		if (!editing && !(form.image instanceof File)) {
			err.image = "La imagen del curso es obligatoria.";
		}
		if (form.image instanceof File && !form.image.type.startsWith("image/")) {
			err.image = "El archivo debe ser una imagen.";
		}
		if (
			form.start_date &&
			form.end_date &&
			isValidDateString(form.start_date) &&
			isValidDateString(form.end_date) &&
			form.end_date < form.start_date
		) {
			err.end_date = "La fecha de término debe ser igual o posterior a la fecha de inicio.";
		}
		if (
			form.enrollment_start_date &&
			form.enrollment_end_date &&
			isValidDateString(form.enrollment_start_date) &&
			isValidDateString(form.enrollment_end_date) &&
			form.enrollment_end_date < form.enrollment_start_date
		) {
			err.enrollment_end_date = "El cierre de postulación debe ser igual o posterior al inicio.";
		}
		if (
			form.enrollment_end_date &&
			form.start_date &&
			isValidDateString(form.enrollment_end_date) &&
			isValidDateString(form.start_date) &&
			form.enrollment_end_date >= form.start_date
		) {
			err.start_date = "La fecha de inicio del curso debe ser posterior al cierre de postulación.";
		}
		setFieldErrors(err);
		return Object.keys(err).length === 0;
	}

	async function onSubmit(e, isDraft = false) {
		if (e && e.preventDefault) e.preventDefault();
		if (!isDraft && !validateForm()) return;

		setSaving(true);
		setAlert(null);
		const finalCode = form.code.trim() || generateCode();
		const finalName = form.name.trim() || "Borrador de curso";
		const finalDesc = getPlainTextFromHtml(form.description).trim() ? form.description : "<p>Borrador automático.</p>";
		const finalObj = getPlainTextFromHtml(form.objectives).trim() ? form.objectives : "<p>Borrador automático.</p>";
		const finalDate = new Date().toISOString().split('T')[0];

		const payload = {
			...form,
			code: isDraft ? finalCode : form.code,
			name: isDraft ? finalName : form.name,
			description: isDraft ? finalDesc : form.description,
			objectives: isDraft ? finalObj : form.objectives,
			is_active: isDraft ? false : form.is_active,
			price_amount:
				form.price_type === "paid" && form.price_amount !== ""
					? Number(form.price_amount)
					: null,
			duration_hours:
				form.duration_hours !== ""
					? Number(form.duration_hours) || null
					: isDraft ? 1 : null,
			start_date: isDraft ? (form.start_date || finalDate) : (form.start_date || null),
			end_date: isDraft ? (form.end_date || finalDate) : (form.end_date || null),
			schedule: isAsyncCourseModality(form.modality)
				? ""
				: isDraft
					? form.schedule || "Horario por definir"
					: form.schedule || null,
			graduation_requirements: form.graduation_requirements || null,
			teacher:
				form.teacher != null && form.teacher !== ""
					? form.teacher
					: editing
						? null
						: undefined,
			enrollment_start_date: isDraft ? (form.enrollment_start_date || finalDate) : (form.enrollment_start_date || null),
			enrollment_end_date: form.enrollment_end_date || null,
			enrollment_url: "https://isomnus.cl/admision",
			max_students:
				form.max_students !== ""
					? Number(form.max_students) || null
					: isDraft ? 1 : null,
		};
		if (!(payload.image instanceof File) && !(payload.image instanceof Blob)) delete payload.image;

		try {
			if (form.image instanceof File) {
				try {
					payload.image = await convertToWebp(form.image, 0.8);
				} catch (error) {
					console.error("No se pudo convertir a WebP la imagen, enviando original", error);
					payload.image = form.image;
				}
			} else {
				delete payload.image;
			}

			if (editing) {
				await updateCourseTemplate(editing.id, payload);
				setAlert({
					type: "success",
					msg: isDraft ? "Borrador actualizado correctamente. (Visible en Inactivos)" : "Curso actualizado correctamente.",
				});
				if (isDraft) setShowInactiveCourses(true);
			} else {
				await createCourseTemplate(payload);
				setAlert({
					type: "success",
					msg: isDraft ? "Borrador guardado automáticamente. (Visible en Inactivos)" : "Curso creado correctamente.",
				});
				if (isDraft) setShowInactiveCourses(true);
			}
			setModalOpen(false);
			setFieldErrors({});
			setEditing(null);
			setForm(emptyForm);
			await refresh();
		} catch (err) {
			const d = err?.response?.data;
			if (!err?.response && typeof err?.message === "string") {
				setAlert({ type: "danger", msg: err.message });
				setFieldErrors({});
			} else {
				const apiFieldErrors = getApiFieldErrors(d);
				setFieldErrors((prev) => ({ ...prev, ...apiFieldErrors }));

				if (Object.keys(apiFieldErrors).length > 0) {
					setAlert(null);
				} else {
					const msg =
						typeof d?.detail === "string"
							? d.detail
							: SAVE_FALLBACK_MESSAGE;
					setAlert({ type: "danger", msg });
				}
			}
		} finally {
			setSaving(false);
		}
	}

	function openDeleteConfirm(item) {
		setItemToDelete(item);
	}

	async function confirmDelete() {
		if (!itemToDelete) return;
		setDeleteLoading(true);
		try {
			await deleteCourseTemplate(itemToDelete.id);
			setAlert({
				type: "success",
				msg: "Curso desactivado correctamente.",
			});
			setItemToDelete(null);
			await refresh();
		} catch {
			setAlert({
				type: "danger",
				msg: "No se pudo desactivar el curso.",
			});
		} finally {
			setDeleteLoading(false);
		}
	}

	function openActivateConfirm(item) {
		setItemToActivate(item);
	}

	async function confirmActivate() {
		if (!itemToActivate) return;
		setActivateLoading(true);
		try {
			await updateCourseTemplate(itemToActivate.id, { is_active: true });
			setAlert({
				type: "success",
				msg: "Curso activado correctamente.",
			});
			setItemToActivate(null);
			await refresh();
		} catch (err) {
			setAlert({
				type: "danger",
				msg:
					err?.response?.data?.detail ||
					"No se pudo activar el curso.",
			});
		} finally {
			setActivateLoading(false);
		}
	}

	const filtered = useMemo(() => {
		const visibleItems = showInactiveCourses
			? items
			: items.filter((item) => item.is_active !== false);
		const q = search.trim().toLowerCase();
		if (!q) return visibleItems;
		return visibleItems.filter(
			(x) => {
				const modality = x.default_run?.modality || "";
				const modalityLabel = traducirModalidad(modality);
				return (
					(x.code ?? "").toLowerCase().includes(q) ||
					(x.name ?? "").toLowerCase().includes(q) ||
					modality.toLowerCase().includes(q) ||
					modalityLabel.toLowerCase().includes(q)
				);
			},
		);
	}, [items, search, showInactiveCourses]);

	const sortedCourses = useMemo(() => {
		function getSortValue(course, field) {
			const counts = {
				enrollment: Number(course.organization_status_counts?.enrollment || 0),
				open: Number(course.organization_status_counts?.open || 0),
				finished: Number(course.organization_status_counts?.finished || 0),
				closed: Number(course.organization_status_counts?.closed || 0),
			};
			if (field === "modality") return course.default_run?.modality ?? "";
			if (field === "enrollment_period") {
				return `${course.default_run?.enrollment_start_date ?? ""}|${course.default_run?.enrollment_end_date ?? ""}`;
			}
			if (field === "organizations_enrollment") return counts.enrollment;
			if (field === "organizations_open") return counts.open;
			if (field === "organizations_finished") return counts.finished;
			if (field === "organizations_closed") return counts.closed;
			if (field === "is_active") return course.is_active ? 1 : 0;
			return (course[field] ?? "").toString().toLowerCase();
		}

		const data = [...filtered];
		data.sort((left, right) => {
			const leftValue = getSortValue(left, sortField);
			const rightValue = getSortValue(right, sortField);

			if (leftValue !== rightValue) {
				if (typeof leftValue === "number" && typeof rightValue === "number") {
					return sortDirection === "asc"
						? leftValue - rightValue
						: rightValue - leftValue;
				}
				if (sortDirection === "asc") return leftValue > rightValue ? 1 : -1;
				return leftValue < rightValue ? 1 : -1;
			}

			const leftActiveRank = left.is_active === false ? 1 : 0;
			const rightActiveRank = right.is_active === false ? 1 : 0;
			if (leftActiveRank !== rightActiveRank) {
				return leftActiveRank - rightActiveRank;
			}
			const leftName = (left.name ?? "").toString().toLowerCase();
			const rightName = (right.name ?? "").toString().toLowerCase();
			if (leftName === rightName) return 0;
			return leftName > rightName ? 1 : -1;
		});
		return data;
	}, [filtered, sortDirection, sortField]);

	useEffect(() => {
		const nextSearch = searchParams.get("q") || "";
		const nextRows = parseRows(searchParams.get("rows"));
		const nextPage = parsePositiveInt(searchParams.get("page"), 1);
		const nextSort = parseSort(searchParams.get("sort"));
		const nextDirection = parseDirection(searchParams.get("dir"));
		const nextShowInactive = parseShowInactive(searchParams.get("inactive"));

		setSearch((prev) => (prev === nextSearch ? prev : nextSearch));
		setRowsPerPage((prev) => (prev === nextRows ? prev : nextRows));
		setCurrentPage((prev) => (prev === nextPage ? prev : nextPage));
		setSortField((prev) => (prev === nextSort ? prev : nextSort));
		setSortDirection((prev) =>
			prev === nextDirection ? prev : nextDirection,
		);
		setShowInactiveCourses((prev) =>
			prev === nextShowInactive ? prev : nextShowInactive,
		);
	}, [searchParams]);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, rowsPerPage, sortField, sortDirection, showInactiveCourses]);

	const totalRows = sortedCourses.length;
	const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

	useEffect(() => {
		if (currentPage > totalPages) setCurrentPage(totalPages);
	}, [currentPage, totalPages]);

	useEffect(() => {
		const nextParams = new URLSearchParams(searchParams);

		if (search.trim()) nextParams.set("q", search.trim());
		else nextParams.delete("q");

		if (rowsPerPage !== COURSES_ROWS_PER_PAGE_DEFAULT) {
			nextParams.set("rows", String(rowsPerPage));
		} else {
			nextParams.delete("rows");
		}

		if (currentPage !== 1) nextParams.set("page", String(currentPage));
		else nextParams.delete("page");

		if (sortField !== "name") nextParams.set("sort", sortField);
		else nextParams.delete("sort");

		if (sortDirection !== "asc") nextParams.set("dir", sortDirection);
		else nextParams.delete("dir");

		if (showInactiveCourses) nextParams.set("inactive", "1");
		else nextParams.delete("inactive");

		if (nextParams.toString() !== searchParams.toString()) {
			setSearchParams(nextParams, { replace: true });
		}
	}, [
		search,
		rowsPerPage,
		currentPage,
		sortField,
		sortDirection,
		showInactiveCourses,
		searchParams,
		setSearchParams,
	]);

	useEffect(() => {
		if (form.image instanceof File) {
			const url = URL.createObjectURL(form.image);
			setImagePreviewSrc(url);
			return () => URL.revokeObjectURL(url);
		}
		setImagePreviewSrc(editing?.image ?? null);
	}, [form.image, editing?.image]);

	function resetTableState() {
		setSearch("");
		setRowsPerPage(COURSES_ROWS_PER_PAGE_DEFAULT);
		setCurrentPage(1);
		setSortField("name");
		setSortDirection("asc");
		setShowInactiveCourses(false);
		setSearchParams(new URLSearchParams(), { replace: true });
	}

	const startIndex = (currentPage - 1) * rowsPerPage;
	const endIndex = Math.min(startIndex + rowsPerPage, totalRows);
	const coursesToShow = sortedCourses.slice(
		startIndex,
		startIndex + rowsPerPage,
	);

	const hasSearchQuery = search.trim().length > 0;
	const emptyCourseMessage = hasSearchQuery
		? "No hay resultados para la búsqueda actual."
		: items.length === 0
			? "No hay cursos registrados."
			: !showInactiveCourses
				? "No hay cursos activos. Activa Mostrar inactivos para revisar históricos."
			: "No hay resultados para los filtros actuales.";

	function getOrganizationStatusCounts(course) {
		return {
			enrollment: Number(course.organization_status_counts?.enrollment || 0),
			open: Number(course.organization_status_counts?.open || 0),
			finished: Number(course.organization_status_counts?.finished || 0),
			closed: Number(course.organization_status_counts?.closed || 0),
		};
	}

	function canDeactivateCourse(course) {
		if (course.is_active === false) return false;
		if (course.can_deactivate === false) return false;
		const counts = getOrganizationStatusCounts(course);
		return counts.open === 0 && counts.finished === 0;
	}

	function tieneContenido(htmlText) {
		return htmlText && htmlText.replace(/<[^>]+>/g, "").trim() !== "";
	}

	function traducirModalidad(mod) {
		if (mod === "sync") return "Sincrónico";
		if (mod === "async") return "Asincrónico";
		if (mod === "mixed") return "Mixto";
		if (mod === "presencial") return "Presencial";
		return "Por definir";
	}

	function traducirEstadoCurso(status) {
		if (status === "open") return "Abierto";
		if (status === "finished") return "Finalizado";
		if (status === "closed") return "Cerrado";
		return status || "Por definir";
	}

	function traducirEstadoRevisionCertificados(status) {
		if (status === "not_applicable") return "No aplica";
		if (status === "pending_review") return "Pendiente de revisión";
		if (status === "approved_for_emission")
			return "Aprobado para emisión";
		return status || "Por definir";
	}

	return (
		<div className="container-fluid">
			<div className="container-principal">
				<div className="w-100">
					<h2 className="m-0">Cursos</h2>
				</div>

				<div className="admin-actions-wrapper w-100">
					<button
						className="admin-btn text-nowrap"
						onClick={openCreate}
					>
						<span>Nuevo curso</span>
					</button>
				</div>
			</div>

			{alert && (
				<div
					className={`alert alert-${alert.type} mt-3 mb-0`}
					role="alert"
				>
					<span>{alert.msg}</span>
				</div>
			)}

			<div className="card mt-3 shadow-sm">
				<div className="card-body p-0">
					<div className="table-toolbar table-toolbar-locked">
						<div className="toolbar-group toolbar-pagination-group">
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(1)}
								disabled={currentPage === 1}
							>
								&lt;&lt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.max(1, prev - 1),
									)
								}
								disabled={currentPage === 1}
							>
								&lt;
							</button>
							<select
								className="form-select toolbar-select toolbar-page-select"
								value={currentPage}
								onChange={(e) =>
									setCurrentPage(Number(e.target.value))
								}
								disabled={totalPages <= 1}
							>
								{Array.from(
									{ length: totalPages },
									(_, idx) => idx + 1,
								).map((page) => (
									<option key={page} value={page}>
										{page}
									</option>
								))}
							</select>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() =>
									setCurrentPage((prev) =>
										Math.min(totalPages, prev + 1),
									)
								}
								disabled={currentPage >= totalPages}
							>
								&gt;
							</button>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => setCurrentPage(totalPages)}
								disabled={currentPage >= totalPages}
							>
								&gt;&gt;
							</button>
						</div>

						<div className="search-group toolbar-search-group d-flex align-items-center gap-2">
							<input
								className="form-control"
								value={search} onChange={(e) => setSearch(e.target.value)}
								placeholder="Buscar por código, nombre o modalidad"
								maxLength={120}
							/>
							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={() => {
									setSearch("");
									setCurrentPage(1);
								}}
								disabled={!search.trim()}
								title="Limpiar búsqueda"
								aria-label="Limpiar búsqueda"
							>
								<i className="ri-eraser-line" />
							</button>
						</div>

						<div className="toolbar-group toolbar-controls-group">
							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Número de filas:</span>
								<select
									className="form-select toolbar-select"
									value={rowsPerPage}
									onChange={(e) =>
										setRowsPerPage(Number(e.target.value))
									}
								>
									<option value={10}>10</option>
									<option value={25}>25</option>
									<option value={50}>50</option>
									<option value={100}>100</option>
								</select>
							</label>

							<label className="d-flex align-items-center gap-2 mb-0">
								<span>Ordenar por:</span>
								<select
									className="form-select toolbar-select"
									value={sortField}
									onChange={(e) =>
										setSortField(e.target.value)
									}
								>
									{COURSE_SORT_OPTIONS.map((option) => (
										<option
											key={option.value}
											value={option.value}
										>
											{option.label}
										</option>
									))}
								</select>
							</label>

							<button
								type="button"
								className="toolbar-order-btn"
								onClick={() =>
									setSortDirection((prev) =>
										prev === "asc" ? "desc" : "asc",
									)
								}
							>
								{sortDirection === "asc" ? "Asc" : "Desc"}
							</button>

							<label className="form-check d-flex align-items-center gap-2 mb-0">
								<input
									className="form-check-input mt-0"
									type="checkbox"
									checked={showInactiveCourses}
									onChange={(e) =>
										setShowInactiveCourses(
											e.target.checked,
										)
									}
								/>
								<span>Mostrar inactivos</span>
							</label>

							<button
								type="button"
								className="toolbar-nav-btn"
								onClick={resetTableState}
								title="Reiniciar tabla"
							>
								Reiniciar
							</button>
						</div>
					</div>

					<div className="d-flex flex-wrap align-items-center gap-3 px-3 py-2 border-top bg-white">
						<span className="d-inline-flex align-items-center gap-1">
							<span className="badge bg-info-subtle text-info-emphasis border">0</span>
							<span>Postulaciones</span>
						</span>
						<span className="d-inline-flex align-items-center gap-1">
							<span className="badge bg-primary-subtle text-primary border">0</span>
							<span>En curso</span>
						</span>
						<span className="d-inline-flex align-items-center gap-1">
							<span className="badge bg-warning-subtle text-warning-emphasis border">0</span>
							<span>Finalizados</span>
						</span>
						<span className="d-inline-flex align-items-center gap-1">
							<span className="badge bg-secondary-subtle text-secondary-emphasis border">0</span>
							<span>Cerrados</span>
						</span>
					</div>

					<div className="table-responsive table-wrapper-scroll">
						<table
							className="table table-sm table-hover mb-0 align-middle table-organizations-nowrap"
							style={{ tableLayout: "fixed", minWidth: 1250 }}
						>
							<thead className="table-light">
								<tr>
									<th style={{ width: "10%" }}>
										<span>Código</span>
									</th>
									<th style={{ width: "20%" }}>
										<span>Nombre</span>
									</th>
									<th style={{ width: "10%" }}>
										<span>Modalidad</span>
									</th>
									<th style={{ width: "15%" }}>
										<span>Fecha postulación</span>
									</th>
									<th
										className="text-center"
										style={{ width: "16%" }}
									>
										<span>Estados</span>
									</th>
									<th
										className="text-center"
										style={{ width: "7%" }}
									>
										<span>Activo</span>
									</th>
									<th
										className="text-center"
										style={{ width: "18%" }}
									>
										<span>Acciones</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td
											colSpan="7"
											className="text-muted p-4"
										>
											<span>Cargando...</span>
										</td>
									</tr>
								) : coursesToShow.length === 0 ? (
									<tr>
										<td
											colSpan="7"
											className="text-muted p-4"
										>
											<span>{emptyCourseMessage}</span>
										</td>
									</tr>
								) : (
									coursesToShow.map((c) => {
										const isInactive = c.is_active === false;
										const organizationCounts =
											getOrganizationStatusCounts(c);
										const allowDeactivate =
											canDeactivateCourse(c);
										return (
										<tr key={c.id}>
											<td className="fw-semibold">
												<span>
													{highlightSearchMatch(
														c.code,
														search,
													)}
												</span>
											</td>
											<td className="text-truncate">
												<span>
													{highlightSearchMatch(
														capitalizeFirstLetter(
															c.name,
														),
														search,
													)}
												</span>
											</td>
											<td>
												<span>
													{c.default_run?.modality
														? traducirModalidad(
															c.default_run
																.modality,
														)
													: "Por definir"}
												</span>
											</td>
											<td>
												<span>
													{formatDateRange(
														c.default_run
															?.enrollment_start_date,
														c.default_run
															?.enrollment_end_date,
													)}
												</span>
											</td>
											<td className="text-center">
												<span className="d-inline-flex justify-content-center align-items-center gap-2 flex-nowrap">
													<span
														className="badge bg-info-subtle text-info-emphasis border"
														title="Postulaciones"
														aria-label={`Postulaciones: ${organizationCounts.enrollment}`}
													>
														{organizationCounts.enrollment}
													</span>
													<span
														className="badge bg-primary-subtle text-primary border"
														title="En curso"
														aria-label={`En curso: ${organizationCounts.open}`}
													>
														{organizationCounts.open}
													</span>
													<span
														className="badge bg-warning-subtle text-warning-emphasis border"
														title="Finalizados"
														aria-label={`Finalizados: ${organizationCounts.finished}`}
													>
														{organizationCounts.finished}
													</span>
													<span
														className="badge bg-secondary-subtle text-secondary-emphasis border"
														title="Cerrados"
														aria-label={`Cerrados: ${organizationCounts.closed}`}
													>
														{organizationCounts.closed}
													</span>
												</span>
											</td>
											<td className="text-center">
												<StatusBadge
													active={Boolean(
														c.is_active,
													)}
													title="Curso activo"
												/>
											</td>
											<td>
												<div className="d-flex justify-content-center align-items-center gap-2 flex-nowrap">
													<button
														className="btn-sm adm-view-btn d-inline-flex align-items-center justify-content-center border-0"
														onClick={() =>
															openDetails(c)
														}
														disabled={isInactive}
														title={
															isInactive
																? "Primero debes activar el curso."
																: "Ver detalles"
														}
														aria-label="Ver detalles"
													>
														<i className="ri-eye-fill" />
													</button>
													<button
														className="btn-sm adm-star-btn d-inline-flex align-items-center justify-content-center border-0"
														onClick={() =>
															openSurveySummary(c)
														}
														disabled={isInactive}
														title={
															isInactive
																? "Primero debes activar el curso."
																: "Ver satisfacción"
														}
														aria-label="Ver satisfacción"
													>
														<i className="ri-star-fill" />
													</button>
													<button
														className="btn-sm adm-upd-btn d-inline-flex align-items-center justify-content-center border-0"
														onClick={() =>
															openEdit(c)
														}
														title="Modificar"
														aria-label="Modificar"
													>
														<i className="ri-edit-fill" />
													</button>
													<button
														className="btn-sm adm-del-btn d-inline-flex align-items-center justify-content-center border-0"
														onClick={() =>
															openDeleteConfirm(c)
														}
														disabled={
															isInactive ||
															deleteLoading ||
															!allowDeactivate
														}
														title={
															isInactive
																? "El curso ya está desactivado."
																: !allowDeactivate
																	? "No se puede desactivar mientras existan organizaciones abiertas o finalizadas."
																: "Desactivar"
														}
														aria-label="Desactivar"
													>
														<i className="ri-close-circle-line" />
													</button>
													<button
														className="btn-sm adm-save-btn d-inline-flex align-items-center justify-content-center border-0"
														onClick={() =>
															openActivateConfirm(c)
														}
														disabled={!isInactive || activateLoading}
														title={
															isInactive
																? "Activar"
																: "El curso ya está activo."
														}
														aria-label="Activar"
													>
														<i className="ri-checkbox-circle-line" />
													</button>
												</div>
											</td>
										</tr>
										);
									})
								)}
							</tbody>
						</table>
					</div>
					<div className="table-footer-summary">
						<span>
							Mostrando {totalRows === 0 ? 0 : startIndex + 1} -{" "}
							{endIndex} de {totalRows} registros
						</span>
					</div>
				</div>
			</div>

			<Modal
				title={
					editing
						? "Editar curso (catálogo)"
						: "Nuevo curso (catálogo)"
				}
				open={modalOpen}
				onClose={closeModal} // Intercepta clics afuera y autoguarda
				footer={
					<>
						<button
							className="btn-sm adm-del-btn"
							onClick={() => closeModal(true)} // "true" obliga a descartar sin guardar
							disabled={saving}
						>
							<span>Cancelar</span>
						</button>
						<button
							className="btn-sm adm-see-btn"
							onClick={(e) => onSubmit(e, true)}
							disabled={saving}
						>
							<span>{saving ? "Guardando..." : "Guardar Borrador"}</span>
						</button>

						<button
							className="btn-sm adm-save-btn"
							onClick={(e) => onSubmit(e, false)}
							disabled={saving}
						>
							<span>{saving ? "Publicando..." : "Publicar"}</span>
						</button>
					</>
				}
			>
				{alert && (
					<div className={`alert alert-${alert.type}`} role="alert">
						<span>{alert.msg}</span>
					</div>
				)}

				<form onSubmit={onSubmit} className="row g-3">
					<div className="col-md-4">
						<label className="form-label d-flex justify-content-between align-items-center">
							<span>
								Código del curso{" "}
								<span className="text-danger">*</span>
							</span>
							<button
								type="button"
								className="btn btn-link btn-sm p-0"
								onClick={() =>
									setForm((p) => ({
										...p,
										code: generateCode(),
									}))
								}
							>
								<span>Generar</span>
							</button>
						</label>
						<input
							className={`form-control ${fieldErrors.code ? "is-invalid" : ""}`}
							value={form.code}
							placeholder="Ingresa codigo" onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))
							}
							maxLength={10}
							aria-invalid={!!fieldErrors.code}
						/>
						{fieldErrors.code && (
							<div className="invalid-feedback">
								{fieldErrors.code}
							</div>
						)}
						<span className="text-muted d-block mt-1 fs-6">
							Identificador corto (máx. 10 caracteres, único). Ej:
							CUR-01, VENTAS.
						</span>
					</div>

					<div className="col-md-8">
						<label className="form-label">
							<span>
								Nombre visible del curso{" "}
								<span className="text-danger">*</span>
							</span>
						</label>
						<input
							className={`form-control ${fieldErrors.name ? "is-invalid" : ""}`}
							value={form.name}
							placeholder="Ingresa name" onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))
							}
							aria-invalid={!!fieldErrors.name}
						/>
						{fieldErrors.name && (
							<div className="invalid-feedback">
								{fieldErrors.name}
							</div>
						)}
					</div>

					<div className="col-12">
						<label className="form-label">
							<span>Descripción / Introducción</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<RichTextEditor
							value={form.description}
							onChange={(value) =>
								setForm((p) => ({
									...p,
									description: value,
								}))
							}
							placeholder="Ej: Introducción, Fundamentación técnica, etc..."
							error={!!fieldErrors.description}
						/>
						{fieldErrors.description && (
							<div className="invalid-feedback">
								{fieldErrors.description}
							</div>
						)}
						<span className="text-muted d-block mt-1 fs-6">
							Texto que explica de qué se trata el curso. Se
							muestra en la ficha del curso.
						</span>
					</div>

					<div className="col-12">
						<label className="form-label">
							<span>Objetivos del curso</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<RichTextEditor
							value={form.objectives}
							onChange={(value) =>
								setForm((p) => ({
									...p,
									objectives: value,
								}))
							}
							placeholder="Ej: 1) Comprender conceptos básicos..."
							error={!!fieldErrors.objectives}
						/>
						{fieldErrors.objectives && (
							<div className="invalid-feedback">
								{fieldErrors.objectives}
							</div>
						)}
						<span className="text-muted d-block mt-1 fs-6 text-justify">
							Enumera qué aprenderá la persona en este curso.
						</span>
					</div>

					<div className="col-12">
						<label className="form-label">
							<span>Contenidos / Temario</span>
						</label>
						<RichTextEditor
							value={form.temario}
							onChange={(value) => {
								setForm((p) => ({ ...p, temario: value }));
								if (fieldErrors.temario) {
									setFieldErrors((p) => ({
										...p,
										temario: undefined,
									}));
								}
							}}
							placeholder="Ej: Módulo 1: Fundamentos..."
							error={!!fieldErrors.temario}
						/>
					</div>

					<div className="col-12">
						<label className="form-label mb-0">
							<span>Imagen del curso</span>{" "}
							{!editing && <span className="text-danger">*</span>}
						</label>
						<span className="text-muted d-block mt-1 fs-6 mb-2">
							Máx. 2MB. Formato ideal: 800 x 800 px (Imagen Cuadrada).
						</span>
						<input
							type="file"
							accept="image/*"
							className={`form-control ${fieldErrors.image ? "is-invalid" : ""}`}
							onChange={(e) => {
								const file = e.target.files?.[0] || null;
								const MAX_FILE_SIZE = 2 * 1024 * 1024;
								if (file && file.size > MAX_FILE_SIZE) {
									setFieldErrors((p) => ({ ...p, image: "Máximo 2MB." }));
									setForm((p) => ({ ...p, image: null }));
									e.target.value = "";
									return;
								}
								setForm((p) => ({ ...p, image: file }));
								setFieldErrors((p) => ({ ...p, image: undefined }));
							}}
						/>
						{fieldErrors.image && (
							<div className="invalid-feedback d-block">
								{fieldErrors.image}
							</div>
						)}
						{imagePreviewSrc && (
							<div className="mt-2">
								<img
									src={imagePreviewSrc}
									alt="Vista previa del curso"
									style={{
										maxWidth: 200,
										maxHeight: 150,
										objectFit: "contain",
										border: "1px solid #dee2e6",
										borderRadius: 4,
									}}
								/>
							</div>
						)}
					</div>

					<div className="col-md-6">
						<label className="form-label">
							<span>Precio</span>
						</label>
						<select
							className="form-select"
							value={form.price_type ?? "free"}
							onChange={(e) =>
								setForm((p) => ({
									...p,
									price_type: e.target.value,
								}))
							}
						>
							<option value="free">Gratis</option>
							<option value="paid">De pago</option>
						</select>
					</div>

					{form.price_type === "paid" && (
						<div className="col-md-6">
							<label className="form-label">
								<span>Monto (CLP)</span>{" "}
								<span className="text-danger">*</span>
							</label>
							<input
								type="number"
								className={`form-control ${fieldErrors.price_amount ? "is-invalid" : ""}`}
								value={form.price_amount ?? ""}
								placeholder="Ingresa un numero" onChange={(e) => {
									setForm((p) => ({
										...p,
										price_amount: e.target.value.replace(
											/\D/g,
											"",
										),
									}));
									if (fieldErrors.price_amount)
										setFieldErrors((p) => ({
											...p,
											price_amount: undefined,
										}));
								}}
								min={1}
								aria-invalid={!!fieldErrors.price_amount}
							/>
							{fieldErrors.price_amount && (
								<div className="invalid-feedback">
									{fieldErrors.price_amount}
								</div>
							)}
						</div>
					)}

					<div className="col-md-6">
						<label className="form-label">
							<span>Modalidad</span>
						</label>
						<select
							className="form-select"
							value={form.modality ?? "presencial"}
							onChange={(e) => {
								const nextModality = e.target.value;
								if (isAsyncCourseModality(nextModality)) {
									setSchedulePickerOpen(false);
								}
								setForm((p) => ({
									...p,
									modality: nextModality,
									schedule: isAsyncCourseModality(nextModality)
										? ""
										: p.schedule,
								}));
								if (fieldErrors.modality || fieldErrors.schedule) {
									setFieldErrors((p) => ({
										...p,
										modality: undefined,
										schedule: undefined,
									}));
								}
							}}
						>
							<option value="presencial">Presencial</option>
							<option value="sync">Sincrónico</option>
							<option value="async">Asincrónico</option>
							<option value="mixed">Mixto</option>
						</select>
						{fieldErrors.modality && (
							<div className="invalid-feedback">
								{fieldErrors.modality}
							</div>
						)}
					</div>

					<div className="col-md-6">
						<label className="form-label">
							<span>Duración (horas)</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							type="number"
							className={`form-control ${fieldErrors.duration_hours ? "is-invalid" : ""}`}
							value={form.duration_hours ?? ""}
							placeholder="Ingresa un numero" onChange={(e) => {
								setForm((p) => ({
									...p,
									duration_hours: e.target.value.replace(
										/\D/g,
										"",
									),
								}));
								if (fieldErrors.duration_hours)
									setFieldErrors((p) => ({
										...p,
										duration_hours: undefined,
									}));
							}}
							min={1}
							aria-invalid={!!fieldErrors.duration_hours}
						/>
						{fieldErrors.duration_hours && (
							<div className="invalid-feedback">
								{fieldErrors.duration_hours}
							</div>
						)}
					</div>

					<div className="col-12 mt-2 pt-3 border-top">
						<h6 className="text-secondary mb-2">
							<span>Fecha Postulación</span>
						</h6>
					</div>
					<div className="col-md-4">
						<label className="form-label">
							<span>Inicio de postulaciones</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							type="date"
							className={`form-control ${fieldErrors.enrollment_start_date ? "is-invalid" : ""}`}
							value={form.enrollment_start_date ?? ""}
							placeholder="Selecciona una fecha" onChange={(e) => setForm((p) => ({
								...p,
								enrollment_start_date: e.target.value,
							}))
							}
							aria-invalid={!!fieldErrors.enrollment_start_date}
						/>
						{fieldErrors.enrollment_start_date && (
							<div className="invalid-feedback">
								{fieldErrors.enrollment_start_date}
							</div>
						)}
					</div>
					<div className="col-md-4">
						<label className="form-label">
							<span>Cierre de postulaciones</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							type="date"
							className={`form-control ${fieldErrors.enrollment_end_date ? "is-invalid" : ""}`}
							value={form.enrollment_end_date ?? ""}
							placeholder="Selecciona una fecha" onChange={(e) => setForm((p) => ({
								...p,
								enrollment_end_date: e.target.value,
							}))
							}
							aria-invalid={!!fieldErrors.enrollment_end_date}
						/>
						{fieldErrors.enrollment_end_date && (
							<div className="invalid-feedback">
								{fieldErrors.enrollment_end_date}
							</div>
						)}
					</div>
					<div className="col-md-4">
						<label className="form-label">
							<span>Postula aquí (URL) </span>
							<span className="text-danger">*</span>
						</label>
						<input
							type="url"
							className={`form-control ${fieldErrors.enrollment_url ? "is-invalid" : ""}`}
							value="https://isomnus.cl/admision"
							readOnly
							disabled
							style={{ backgroundColor: "#e9ecef" }}
						/>
						{fieldErrors.enrollment_url && (
							<div className="invalid-feedback">
								{fieldErrors.enrollment_url}
							</div>
						)}
					</div>

					<div className="col-12 mt-2 pt-3 border-top">
						<h6 className="text-secondary mb-2">
							<span>Inicio del Curso</span>
						</h6>
					</div>
					<div className="col-md-4">
						<label className="form-label">
							<span>Fecha de inicio</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							type="date"
							className={`form-control ${fieldErrors.start_date ? "is-invalid" : ""}`}
							value={form.start_date ?? ""}
							placeholder="Selecciona una fecha" onChange={(e) => setForm((p) => ({
								...p,
								start_date: e.target.value,
							}))
							}
							aria-invalid={!!fieldErrors.start_date}
						/>
						{fieldErrors.start_date && (
							<div className="invalid-feedback">
								{fieldErrors.start_date}
							</div>
						)}
					</div>
					<div className="col-md-4">
						<label className="form-label">
							<span>Fecha de término</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<input
							type="date"
							className={`form-control ${fieldErrors.end_date ? "is-invalid" : ""}`}
							value={form.end_date ?? ""}
							placeholder="Selecciona una fecha" onChange={(e) => setForm((p) => ({
								...p,
								end_date: e.target.value,
							}))
							}
							aria-invalid={!!fieldErrors.end_date}
						/>
						{fieldErrors.end_date && (
							<div className="invalid-feedback">
								{fieldErrors.end_date}
							</div>
						)}
					</div>
					<div className="col-md-4">
						{(() => {
							const isAsync = isAsyncCourseModality(form.modality);
							return (
								<>
						<label className="form-label">
							<span>Horario </span>
							{!isAsync && <span className="text-danger">*</span>}
						</label>
						<div
							className={`rounded border p-2 ${fieldErrors.schedule ? "border-danger" : ""} ${isAsync ? "bg-light" : ""}`}
						>
							<div className="small text-break mb-2">
								{isAsync ? (
									<span className="text-muted">
										No aplica para cursos asincrónicos.
									</span>
								) : (form.schedule ?? "").trim() ? (
									formatWeeklyScheduleForDisplay(
										form.schedule,
									)
								) : (
									<span className="text-muted">
										Sin horario definido
									</span>
								)}
							</div>
							<button
								type="button"
								className="adm-see-btn"
								onClick={() => setSchedulePickerOpen(true)}
								disabled={isAsync}
							>
								<span>Definir horario semanal</span>
							</button>
						</div>
						<span className="text-muted d-block mt-1 fs-6">
							{isAsync
								? "En modalidad asincrónica no se seleccionan días ni bloques horarios."
								: "Lunes a sábado: elige día y hora en el calendario. Los días sin hora no tienen clase."}
						</span>
						{fieldErrors.schedule && (
							<div className="invalid-feedback d-block">
								{fieldErrors.schedule}
							</div>
						)}
								</>
							);
						})()}
					</div>

					<div className="col-12 mt-2 pt-3 border-top"></div>
					<div className="col-md-6">
						<label className="form-label">
							<span>Docente asignado</span>{" "}
							<span className="text-danger">*</span>
						</label>
						<select
							className={`form-select ${fieldErrors.teacher ? "is-invalid" : ""}`}
							value={form.teacher ?? ""}
							onChange={(e) =>
								setForm((p) => ({
									...p,
									teacher: e.target.value,
								}))
							}
							aria-describedby="teacher-help"
							aria-invalid={!!fieldErrors.teacher}
						>
							<option value="">Selecciona un docente</option>
							{teachers.map((t) => (
								<option key={t.id} value={t.id}>
									{t.first_name && t.last_name
										? `${capitalizeWords(t.first_name)} ${capitalizeWords(t.last_name)}`
										: t.username}
								</option>
							))}
						</select>
						{fieldErrors.teacher && (
							<div className="invalid-feedback">
								{fieldErrors.teacher}
							</div>
						)}
						<div id="teacher-help" className="form-text">
							<span className="text-muted d-block mt-1 fs-6 text-justify">
								Sin docente, los estudiantes no podrán enviar
								mensajes ni ver contenido de evaluación
								correctamente. Asigna uno para que el curso
								funcione como el de datos de prueba.
							</span>
						</div>
					</div>
					<div className="col-md-6">
						<label className="form-label">
							<span>Cupos </span>
							<span className="text-danger">*</span>
						</label>
						<input
							type="number"
							className={`form-control ${fieldErrors.max_students ? "is-invalid" : ""}`}
							value={form.max_students ?? ""}
							placeholder="Ingresa un numero" onChange={(e) => {
								setForm((p) => ({
									...p,
									max_students: e.target.value.replace(
										/\D/g,
										"",
									),
								}));
								if (fieldErrors.max_students)
									setFieldErrors((p) => ({
										...p,
										max_students: undefined,
									}));
							}}
							aria-invalid={!!fieldErrors.max_students}
						/>
						{fieldErrors.max_students && (
							<div className="invalid-feedback">
								{fieldErrors.max_students}
							</div>
						)}
					</div>

					<div className="col-12 mt-2 pt-3 border-top">
						<p className="text-muted d-block mt-1 fs-6 text-justify mb-0">
							<strong>Flujo por organización:</strong> Si este curso se
							asigna a una organización (Organización → curso asignado), la
							estructura (unidad, módulo, contenido, examen) y la
							matrícula de estudiantes se gestionan desde el
							portal de la organización: el representante sube el Excel
							con estudiantes y realiza la matrícula masiva al
							curso. Los estudiantes verán el curso en el portal
							académico (Mis Cursos) con contenido y mensajería
							listos.
						</p>
					</div>

					<div className="col-12">
						<div className="form-check">
							<input
								className="form-check-input"
								type="checkbox"
								checked={form.is_active}
								onChange={(e) =>
									setForm((p) => ({
										...p,
										is_active: e.target.checked,
									}))
								}
								id="isActive"
							/>
							<label
								className="form-check-label"
								htmlFor="isActive"
							>
								<span>Curso activo (visible para uso)</span>
							</label>
						</div>
					</div>

					<button type="submit" className="d-none">
						<span>enviar</span>
					</button>
				</form>
			</Modal>

			<WeeklyScheduleModal
				open={schedulePickerOpen}
				onClose={() => setSchedulePickerOpen(false)}
				initialSchedule={form.schedule ?? ""}
				onApply={(s) => {
					setForm((p) => ({ ...p, schedule: s }));
					setFieldErrors((prev) => ({
						...prev,
						schedule: undefined,
					}));
				}}
			/>

			{/* Modal de detalles del curso y estudiantes */}
			<Modal
				title="Detalles del Curso"
				open={detailsOpen}
				onClose={() => setDetailsOpen(false)}
				dialogClassName="modal-dialog modal-dialog-centered modal-lg"
				footer={
					<button
						className="adm-del-btn"
						onClick={() => setDetailsOpen(false)}
					>
						<span>Cerrar</span>
					</button>
				}
			>
				{selectedCourseDetail && (
					<div className="font-n">
						<h3 className="mb-4">
							{capitalizeFirstLetter(selectedCourseDetail?.name)}
						</h3>
						<div className="descrip-course-img mb-4 d-flex justify-content-center w-100">
							<div className="container-img-course">
								<img
									src={selectedCourseDetail?.image || "/img/course/1.webp"}
									alt={capitalizeFirstLetter(selectedCourseDetail?.name || "Curso")}
								/>
							</div>
						</div>
						<div className="desc-first">
							<hr className="course-divider-line" />
							<div>
								<div className="row-info">
									<p>Código:</p>
									<span>{selectedCourseDetail.code}</span>
								</div>
								<div className="row-info mt-2">
									<p>Duracipon (horas):</p>
									<span>{selectedCourseDetail.duration_hours || "—"}</span>
								</div>
								<div className="row-info mt-2">
									<p>Horario de Clases:</p>
									<span>{formatWeeklyScheduleForDisplay(selectedCourseDetail?.default_run?.schedule) || "Sin horario definido"}</span>
								</div>
								<div className="row-info mt-2">
									<p>Modalidad:</p>
									<span>{selectedCourseDetail?.default_run?.modality ? traducirModalidad(selectedCourseDetail.default_run.modality) : "Por definir"}</span>
								</div>
								<div className="row-info mt-2">
									<p>Precio:</p>
									<span>{formatCoursePriceLabel(selectedCourseDetail,)}</span>
								</div>

							</div>
						</div>

						<div className="desc-description me-4">
							<hr className="course-divider-line" />
							<div>
								<p className="font-tittle-desc">Descripción</p>
								<div>
									{tieneContenido(selectedCourseDetail.description) ? (
										<div
											className="valores-body-text"
											dangerouslySetInnerHTML={{
												__html: DOMPurify.sanitize(selectedCourseDetail.description),
											}}
										/>
									) : (
										<p className="text-muted">
											Sin descripción disponible.
										</p>
									)}
								</div>
							</div>

						</div>
						<div className="desc-description me-4">
							<hr className="course-divider-line" />
							<div>
								<p className="font-tittle-desc">Objetivos</p>
								<div>
									{tieneContenido(selectedCourseDetail.objectives) ? (
										<div
											className="valores-body-text"
											dangerouslySetInnerHTML={{
												__html: DOMPurify.sanitize(selectedCourseDetail.objectives),
											}}
										/>
									) : (
										<p className="text-muted">
											Sin objetivos definidos.
										</p>
									)}
								</div>
							</div>
						</div>
						{selectedCourseDetail?.default_run && (
							<div className="desc-description me-4">
								<hr className="course-divider-line" />
								<div>
									<p className="font-tittle-desc">Certificación</p>
									<div>
										<div className="row-info mt-2">
											<p>Estado del curso:</p>
											<span>
												{traducirEstadoCurso(
													selectedCourseDetail.default_run.status,
												)}
											</span>
										</div>
										<div className="row-info mt-2">
											<p>Revisión administrativa:</p>
											<span>
												{traducirEstadoRevisionCertificados(
													selectedCourseDetail.default_run
														.certificate_review_status,
												)}
											</span>
										</div>
										<div className="row-info mt-2">
											<p>Emisión habilitada:</p>
											<StatusBadge
												active={Boolean(
													selectedCourseDetail.default_run
														.certificate_emission_enabled,
												)}
												trueLabel="Sí"
												falseLabel="No"
												title={
													selectedCourseDetail.default_run
														.certificate_emission_enabled
														? "La emisión está habilitada"
														: "La emisión está deshabilitada"
												}
											/>
										</div>
									</div>
								</div>
							</div>
						)}
						<div className="desc-description me-4">
							<hr className="course-divider-line" />
							<div>
								<p className="font-tittle-desc">
									Temario / Contenidos
								</p>
								<div>
									{tieneContenido(selectedCourseDetail.temario) ? (
										<div
											className="valores-body-text"
											dangerouslySetInnerHTML={{
												__html: DOMPurify.sanitize(selectedCourseDetail.temario),
											}}
										/>
									) : (
										<p className="text-muted">
											Temario en desarrollo.
										</p>
									)}
								</div>
							</div>
						</div>


					</div>
				)}
			</Modal>

			<ConfirmModal
				open={!!itemToDelete}
				onClose={() => !deleteLoading && setItemToDelete(null)}
				onConfirm={confirmDelete}
				title="Desactivar curso"
				message={
					itemToDelete ? (
						<span>
							¿Desactivar el curso "{itemToDelete.code} -{" "}
							{itemToDelete.name}"? Se conservará su historial,
							ediciones, matrículas y certificados. Sus
							asignaciones activas a organizaciones quedarán
							desactivadas. Esta acción solo está permitida
							cuando no existen organizaciones con el curso
							abierto o finalizado.
						</span>
					) : (
						""
					)
				}
				confirmLabel={<span>Desactivar</span>}
				variant="danger"
				loading={deleteLoading}
			/>
			<ConfirmModal
				open={!!itemToActivate}
				onClose={() => !activateLoading && setItemToActivate(null)}
				onConfirm={confirmActivate}
				title="Activar curso"
				message={
					itemToActivate ? (
						<span>
							¿Activar el curso "{itemToActivate.code} -{" "}
							{itemToActivate.name}"? Podrá volver a usarse como
							curso de catálogo. Revisa manualmente sus
							asignaciones a organizaciones antes de dejarlo
							operativo para matrículas.
						</span>
					) : (
						""
					)
				}
				confirmLabel={<span>Activar</span>}
				variant="success"
				loading={activateLoading}
			/>
			<Modal
				title={`Satisfacción: ${surveyModalTitle}`}
				open={isSurveyModalOpen}
				onClose={() => setIsSurveyModalOpen(false)}
				dialogClassName="modal-dialog modal-dialog-centered modal-lg"
				footer={
					<button
						type="button"
						className="adm-del-btn"
						onClick={() => setIsSurveyModalOpen(false)}
					>
						<span>Cerrar</span>
					</button>
				}
			>
				{surveySummaryLoading ? (
					<div className="text-center p-4"><span>Cargando...</span></div>
				) : surveySummaryData ? (
					surveySummaryData.total === 0 ? (
						<div className="text-center p-4"><span>No hay respuestas aún.</span></div>
					) : (
						<div>
							<div className="mb-4">
								<span className="mb-1"><span>Respuestas totales: {surveySummaryData.total}</span></span>
							</div>
							
							<div className="row g-3 mb-4">
								<div className="col-md-6">
									<div className="p-3 border rounded">
										<div className="fw-semibold mb-2"><span>¿Cómo te pareció el curso en general?</span></div>
										<div>
											<span className="fs-5"><span>*</span>{surveySummaryData.overall_satisfaction?.value}<span>*</span></span>
										</div>
										<div>
											<span className="text-muted fs-6 mt-1">{surveySummaryData.overall_satisfaction?.count} de {surveySummaryData.total} personas dieron esta respuesta.</span>
										</div>
									</div>
								</div>
								
								<div className="col-md-6">
									<div className="p-3 border rounded">
										<div className="fw-semibold mb-2"><span>Calidad del contenido</span></div>
										<div>
											<span className="fs-5"><span>*</span>{surveySummaryData.content_quality?.value}<span>*</span></span>
										</div>
										<div>
											<span className="text-muted fs-6 mt-1">{surveySummaryData.content_quality?.count} de {surveySummaryData.total} personas dieron esta respuesta.</span>
										</div>
									</div>
								</div>

								<div className="col-md-6">
									<div className="p-3 border rounded">
										<div className="fw-semibold mb-2"><span>Evaluación del docente</span></div>
										<div>
											<span className="fs-5"><span>*</span>{surveySummaryData.teacher_evaluation?.value}<span>*</span></span>
										</div>
										<div>
											<span className="text-muted fs-6 mt-1">{surveySummaryData.teacher_evaluation?.count} de {surveySummaryData.total} personas dieron esta respuesta.</span>
										</div>
									</div>
								</div>

								<div className="col-md-6">
									<div className="p-3 border rounded">
										<div className="fw-semibold mb-2"><span>Experiencia con la plataforma</span></div>
										<div>
											<span className="fs-5"><span>*</span>{surveySummaryData.platform_experience?.value}<span>*</span></span>
										</div>
										<div>
											<span className="text-muted fs-6 mt-1">{surveySummaryData.platform_experience?.count} de {surveySummaryData.total} personas dieron esta respuesta.</span>
										</div>
									</div>
								</div>
								
								<div className="col-md-6">
									<div className="p-3 border rounded">
										<div className="fw-semibold mb-2"><span>¿Recomendaría el curso?</span></div>
										<div>
											<span className="fs-5"><span>*</span>{surveySummaryData.recommendation?.value ? "Sí" : "No"}<span>*</span></span>
										</div>
										<div >
											<span className="text-muted fs-6 mt-1">{surveySummaryData.recommendation?.count} de {surveySummaryData.total} personas dieron esta respuesta.</span>
										</div>
									</div>
								</div>
							</div>
							
							<div className="mt-4">
								<h6 className="fw-semibold"><span>Comentarios Adicionales</span></h6>
								{surveySummaryData.comments && surveySummaryData.comments.length > 0 ? (
									<div className="table-table-responsive table-wrapper-scroll" >
										<table className="table table-hover align-middle mb-0">
											<thead className="table-light">
												<tr>
													<th><span>Comentario</span></th>
												</tr>
											</thead>
											<tbody>
												{surveySummaryData.comments.map((comment, idx) => (
													<tr key={idx}>
														<td><span>{capitalizeFirstLetter(comment)}</span></td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								) : (
									<p className="text-muted small">No hay comentarios adicionales.</p>
								)}
							</div>
						</div>
					)
				) : null}
			</Modal>
		</div>
	);
}
